// Application-level user record (public.users) — distinct from Supabase Auth.
// Stores per-user preferences like the email signature HTML used when
// sending client emails via Microsoft Graph.

import { supabase } from '../supabaseClient';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  preferences: Record<string, unknown>;
}

export const userService = {
  async getCurrentProfile(): Promise<UserProfile | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('users')
      .select('id, email, name, preferences')
      .eq('id', user.id)
      .single();
    if (error) {
      console.warn('Failed to load user profile:', error);
      return null;
    }
    return {
      ...data,
      preferences: (data.preferences as Record<string, unknown>) ?? {},
    };
  },

  async getEmailSignature(): Promise<string> {
    const profile = await this.getCurrentProfile();
    if (!profile) return '';
    const sig = profile.preferences?.email_signature_html;
    return typeof sig === 'string' ? sig : '';
  },

  async setEmailSignature(html: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Read-merge-write the preferences JSONB so we don't clobber sibling keys.
    const { data: current, error: readError } = await supabase
      .from('users')
      .select('preferences')
      .eq('id', user.id)
      .single();
    if (readError) throw readError;

    const next = {
      ...((current?.preferences as Record<string, unknown>) ?? {}),
      email_signature_html: html,
    };
    const { error: writeError } = await supabase
      .from('users')
      .update({ preferences: next })
      .eq('id', user.id);
    if (writeError) throw writeError;
  },
};
